import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { resetStores } from '@datorama/akita';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { catchError, filter, finalize, map, of, switchMap, take, tap } from 'rxjs';

import { UserQuery, UserState } from '@common/store/user.store';
import { UserProfile } from '@common/types';
import {
  STORAGE_KEY_ENTITY_ID,
  STORAGE_KEY_IS_LOGGED_IN,
  STORAGE_KEY_USER_ID,
  URL_FRAGMENT_REDIRECT_URL,
} from '@common/util/constants';
import { getNestedValue, isEmpty } from '@common/util/util';

import { AuthDataService } from './auth-data.service';
import { DomainService } from './domain.service';
import { LocalStorageService } from './localstorage.service';
import { SystemNotificationService } from './system-notification.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(
    private oidcSecurityService: OidcSecurityService,
    private userStore: UserState,
    private userQuery: UserQuery,
    private httpClient: HttpClient,
    private router: Router,
    private domainService: DomainService,
    private systemNotification: SystemNotificationService,
    private localstorageService: LocalStorageService,
    private authDataService: AuthDataService,
  ) {}

  authWatcher$ = this.authDataService.getAuthData().pipe(
    filter((authData) => !isEmpty(authData)),
    map((authData) => authData.client_id),
    switchMap((clientId) => this.localstorageService.watch$(`0-${clientId}`)),
  );

  accessToken$ = this.authWatcher$.pipe(
    map((oidcStorageData) => {
      const oidcData = oidcStorageData as Record<string, object>;
      const tokenData = {
        access_token_expires_at: getNestedValue(oidcData, 'access_token_expires_at') as number,
        access_token: getNestedValue(oidcData, 'authnResult.access_token') as string,
      };

      return tokenData;
    }),
  );

  /* isUserAuthenticated$ = this.oidcSecurityService.isAuthenticated$.pipe(
    map((authInfo) => {
      return authInfo.isAuthenticated;
    }),
  ); */

  isUserAuthenticated$ = this.accessToken$.pipe(
    map((tokenData) => this.validateAccessToken(tokenData)),
    tap((isAuthenticated) => {
      if (!isAuthenticated) {
        this.setLoginStatus(false);
      }
    }),
  );

  private userProfile$ = this.userQuery.user$;
  private _perceivedAuthenticationState = false;

  get perceivedAuthenticationState() {
    return this._perceivedAuthenticationState;
  }

  authUser(queryParams?: { [key: string]: string }) {
    return of(queryParams).pipe(
      switchMap((params) => {
        const { state, code } = params || {};

        if (state && code) {
          return of(null);
        } else {
          return this.oidcSecurityService.forceRefreshSession().pipe(
            catchError((error) => {
              this.setLoginStatus(false);
              this._perceivedAuthenticationState = false;
              console.error('error in session refresh', error);
              throw error;
            }),
          );
        }
      }),
      switchMap(() => {
        return this.oidcSecurityService.checkAuth().pipe(
          take(1),
          tap((loginResponse) => {
            if (!isEmpty(loginResponse) && loginResponse.isAuthenticated) {
              this._perceivedAuthenticationState = true;
              this.setLoginStatus(true);
            }
          }),
          switchMap(() => this.getUserInfo()),
          catchError((error) => {
            console.error('CheckAuth Error:', error);
            throw error;
          }),
        );
      }),
    );
  }

  login(redirectUrl?: { pathname: string; hash?: string }) {
    // const domainData = this.domainState.getValue();

    this.authDataService
      .getAuthData()
      .pipe(
        filter((authData) => !isEmpty(authData)),
        switchMap((authData) => {
          const preauthUri = authData.preauth_uri;
          const targetUrl = new URL(`${window.location.origin}/auth`);
          const { pathname, hash } = redirectUrl?.pathname ? redirectUrl : window.location;
          targetUrl.searchParams.set(URL_FRAGMENT_REDIRECT_URL, `${pathname}${hash || ''}`);

          return this.oidcSecurityService.getConfiguration().pipe(
            take(1),
            tap((config) => {
              if (config?.configId) {
                this.systemNotification.showSpinner('Redirecting to login...');

                this.oidcSecurityService.authorize(config.configId, {
                  urlHandler: (url) => {
                    const configId = config.configId;
                    const existingConfig = localStorage.getItem(configId!);
                    const configJson = JSON.parse(existingConfig!);

                    if (existingConfig) {
                      const state = configJson['authStateControl'];

                      this.httpClient
                        .post(preauthUri, {
                          state,
                          target: targetUrl.toString(),
                        })
                        .pipe(
                          take(1),
                          tap(() => {
                            window.location.href = url;
                          }),
                          catchError((error) => {
                            console.error('Preauth Error:', error);
                            return of(null);
                          }),
                        )
                        .subscribe();
                    }
                  },
                });
              }
            }),
          );
        }),
      )
      .subscribe();
  }

  logout(type: 'session' | 'global' = 'global') {
    this._perceivedAuthenticationState = false;
    this.setLoginStatus(false);

    this.oidcSecurityService
      .getRefreshToken()
      .pipe(
        take(1),
        switchMap((refreshToken) => {
          const body: { type: 'session' | 'global'; refresh_token?: string } = {
            type,
          };

          if (type === 'session') {
            if (refreshToken) {
              body.refresh_token = refreshToken;
            }
          }

          const overlayRef = this.systemNotification.showSpinner('Logging out...');

          return this.httpClient.post('/api/auth/logout', body).pipe(
            take(1),
            tap(() => {
              // Reset all Akita stores - this will update authorisation.service observables
              resetStores();

              if (window.localStorage) {
                window.localStorage.clear();
              }
            }),
            switchMap(() => {
              return this.domainService.fetchDomainInfo().pipe(
                catchError((domainError) => {
                  console.error('Domain Info Error:', domainError);
                  return of(null);
                }),
              );
            }),
            switchMap(() => {
              // Explicitly log out from OIDC to update authentication state
              return this.oidcSecurityService.checkAuth().pipe(
                catchError((oidcError) => {
                  console.error('OIDC Logout Error:', oidcError);
                  return of(null);
                }),
              );
            }),
            catchError((error) => {
              console.error('Logout Error:', error);
              if (window.localStorage) {
                window.localStorage.clear();
              }
              return of(null);
            }),
            finalize(() => {
              overlayRef.close();
              this.router.navigate(['/']);
            }),
          );
        }),
      )
      .subscribe();
  }

  getUserInfo(refresh = false) {
    if (!isEmpty(this.userQuery.getValue()) && !refresh) {
      return this.userProfile$;
    }

    return this.httpClient.get<UserProfile>('/api/user').pipe(
      take(1),
      tap((userProfile: UserProfile) => {
        if (!isEmpty(userProfile)) {
          this.userStore.update(userProfile);
          this.localstorageService.set(STORAGE_KEY_USER_ID, userProfile.user_id);
          this.localstorageService.set(STORAGE_KEY_ENTITY_ID, userProfile.entity?.entity_id);
        }
      }),
      switchMap(() => {
        return this.userProfile$;
      }),
      catchError((error) => {
        console.error('Get User Info Error:', error);
        return of(null);
      }),
    );
  }

  updateUserInfo(userData: { first_name: string; last_name: string }) {
    this.userStore.update({
      ...(this.userQuery.getValue() ? this.userQuery.getValue() : ({} as UserProfile)),
      ...userData,
    });
  }

  getLoginStatus() {
    return this.localstorageService.get(STORAGE_KEY_IS_LOGGED_IN);
  }

  setLoginStatus(status: boolean) {
    this.localstorageService.set(STORAGE_KEY_IS_LOGGED_IN, status);
  }

  private validateAccessToken(tokenData: { access_token_expires_at: number; access_token: string }): boolean {
    if (!tokenData.access_token) {
      return false;
    }

    const now = Date.now();
    const expiresAt = tokenData.access_token_expires_at;

    return now < expiresAt;
  }
}
